import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/context/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

const Spinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-nwsisd-dark-blue"></div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const { user, isLoading, hasRole, userRoles } = useAuth();
  const location = useLocation();

  // After a hard reload the roles fetch can finish (isLoading=false) with userRoles still
  // empty for a beat — or fail transiently. Bouncing in that window is the cold-reload bug
  // that drops the route + `?tab=`. Give roles a short grace period to populate before
  // treating "no role" as authoritative; fall back to the redirect if they never arrive.
  const rolesPending = !!user && !!allowedRoles && (!userRoles || userRoles.length === 0);
  const [graceExpired, setGraceExpired] = useState(false);
  useEffect(() => {
    if (!rolesPending) { setGraceExpired(false); return; }
    const t = setTimeout(() => setGraceExpired(true), 2000);
    return () => clearTimeout(t);
  }, [rolesPending]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if user has required roles
  if (allowedRoles && !hasRole(allowedRoles)) {
    if (rolesPending && !graceExpired) {
      // Roles may still be resolving right after a reload — wait instead of bouncing.
      return <Spinner />;
    }
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
